import { use, useEffect, useMemo, useState } from 'react';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import { jsonParseWithLowerCaseKeys } from 'shared/utils/string-utils';

import WanderingDetectionConfiguration from 'features/residents/models/configurations/wandering-detection-configuration';
import WanderingDetectionTimeFrameConfiguration from 'features/residents/models/configurations/wandering-detection-time-frame-configuration';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

type Props = {
  index: number;
};

type WanderingDetectionScenarioConfiguration = {
  configuration: WanderingDetectionConfiguration;
  notInEditMode: boolean;
  updateResidentSettingEditFormScenario: () => void;
  timeFrameConfigurationsChanged: (
    newTimeFrameConfigurations: WanderingDetectionTimeFrameConfiguration[] | null,
    newActiveAllDay: boolean,
  ) => void;
  viewingMode: ViewingMode;
};

export const useWanderingDetectionScenarioConfiguration = ({ index }: Props): WanderingDetectionScenarioConfiguration => {
  const {
    detailEditState: { viewingMode, currentResidentSetting, residentSettingEditForm },
  } = use(ResidentSettingsProviderContext);

  const rawConfiguration = useMemo(
    () => currentResidentSetting?.organisationUnitScenarios[index].configuration,
    [currentResidentSetting, index],
  );

  const [configuration, setConfiguration] = useState(jsonParseWithLowerCaseKeys(rawConfiguration));

  const notInEditMode = viewingMode === 'viewing';

  const updateScenarioConfiguration = (newConfiguration: unknown): void => {
    setConfiguration(newConfiguration as never);
  };

  const updateResidentSettingEditFormScenario = (): void => {
    const WanderingDetectionConfiguration = jsonParseWithLowerCaseKeys(
      residentSettingEditForm.getValues(`organisationUnitScenarios.${index}.configuration`),
    );

    residentSettingEditForm.setValue(`organisationUnitScenarios.${index}.configuration`, JSON.stringify(configuration), {
      shouldDirty: JSON.stringify(WanderingDetectionConfiguration) !== JSON.stringify(configuration),
    });
  };

  const timeFrameConfigurationsChanged = (newTimeFrameConfigurations: WanderingDetectionTimeFrameConfiguration[] | null): void => {
    const newConfig = {
      ...configuration,
      wanderingDetectionTimeFrameConfigurations: newTimeFrameConfigurations,
    };

    updateScenarioConfiguration(newConfig);
    residentSettingEditForm.setValue(`organisationUnitScenarios.${index}.configuration`, JSON.stringify(newConfig), {
      shouldDirty: true,
    });
  };

  useEffect(() => {
    residentSettingEditForm.clearErrors(`organisationUnitScenarios.${index}`);
    setConfiguration(jsonParseWithLowerCaseKeys(rawConfiguration) as WanderingDetectionConfiguration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawConfiguration, viewingMode]);

  return {
    configuration,
    notInEditMode,
    updateResidentSettingEditFormScenario,
    timeFrameConfigurationsChanged,
    viewingMode,
  };
};
